import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import styles from "./Toast.module.scss";

const DEFAULT_OPTIONS = {
  position: "top-right",
  autoClose: 8000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: false,
  draggable: true,
  icon: true,
  style: {
    borderRadius: '12px',
  }
};

function ToastTags({ tags }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const isOverflowing = container.scrollHeight > container.clientHeight;
        setNeedsExpansion(isOverflowing);
      }
    };

    checkOverflow();

    const handleResize = () => {
      requestAnimationFrame(checkOverflow);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tags]);

  const handleToggleExpand = (e) => {
    setIsExpanded(prev => !prev);
  };

  return (
    <div className={styles.toastTags}>
      <div className={styles.tagsLabel}>Subscribed to:</div>
      <div className={styles.tagsList}>
        <div
          ref={containerRef}
          className={`${styles.tagsContainer} ${isExpanded ? styles.expanded : ''}`}
        >
          {tags.map((tag, index) => (
            <span key={`${tag}-${index}`} className={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
        {needsExpansion && (
          <button
            className={styles.expandToggle}
            onClick={handleToggleExpand}
            type="button"
          >
            {isExpanded ? 'Show Less' : `Show All (${tags.length})`}
            <span className={styles.expandIcon}>
              {isExpanded ? "▲\u00A0" : "▼\u00A0"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

function ToastContent({ heading, subHeading, message, tags, headingClass }) {
  const renderSubHeading = () => {
    if (!subHeading) return null;
    return <div className={styles.toastSubHeading}>{subHeading}</div>;
  };

  return (
    <div className={styles.toastBody}>
      {heading && (
        <div className={styles.toastHeadingContainer}>
          <div className={`${styles.toastHeadingPanel} ${headingClass}`}>
            <div className={styles.toastHeading}>
              {heading}
            </div>
          </div>
        </div>
      )}
      {message && <div className={styles.toastMessage}>{message}</div>}
      {tags && tags.length > 0 && <ToastTags tags={tags} />}
      {renderSubHeading()}
    </div>
  );
};

function buildContent(message, options, headingClass) {
  if (options.heading || options.subHeading || options.tags) {
    return <ToastContent
      heading={options.heading}
      subHeading={options.subHeading}
      message={message}
      tags={options.tags}
      headingClass={headingClass}
    />;
  }
  return message;
}

export function useToast() {
  return {
    success: (message, options = {}) =>
      toast.success(buildContent(message, options, styles.toastHeadingPanelSuccess), {
        ...DEFAULT_OPTIONS,
        ...options
      }),

    error: (message, options = {}) =>
      toast.error(buildContent(message, options, styles.toastHeadingPanelError), {
        ...DEFAULT_OPTIONS,
        ...options
      }),

    info: (message, options = {}) =>
      toast.info(buildContent(message, options, styles.toastHeadingPanelInfo), {
        ...DEFAULT_OPTIONS,
        ...options
      }),

    warn: (message, options = {}) =>
      toast.warn(buildContent(message, options, styles.toastHeadingPanelWarn), {
        ...DEFAULT_OPTIONS,
        ...options
      }),

    loading: (message, options = {}) => {
      const id = toast.loading(buildContent(message, options, styles.toastHeadingPanelLoading), {
        ...DEFAULT_OPTIONS,
        autoClose: false,
        closeOnClick: false,
        ...options
      });
      return {
        hide: () => toast.dismiss(id),
        update: (newMessage, newOptions = {}) => {
          toast.update(id, {
            render: buildContent(newMessage, newOptions, styles.toastHeadingPanelSuccess),
            type: newOptions.type || "success",
            isLoading: false,
            autoClose: newOptions.autoClose ?? DEFAULT_OPTIONS.autoClose,
            hideProgressBar: false,
            closeOnClick: false,
            ...newOptions
          });
        }
      };
    },

    dismiss: (id) => toast.dismiss(id),
    dismissAll: () => toast.dismiss()
  };
}